// Browser Notifications utility

export type NotificationPermission = "default" | "granted" | "denied";

export const getNotificationPermission = (): NotificationPermission => {
    if (!("Notification" in window)) {
        return "denied";
    }
    return Notification.permission as NotificationPermission;
};

export const isNotificationSupported = (): boolean => {
    return "Notification" in window;
};

export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
    if (!isNotificationSupported()) {
        return "denied";
    }

    if (Notification.permission === "granted") {
        return "granted";
    }

    if (Notification.permission !== "denied") {
        const permission = await Notification.requestPermission();
        return permission as NotificationPermission;
    }

    return Notification.permission as NotificationPermission;
};

export const sendNotification = (
    title: string,
    options?: NotificationOptions
): Notification | null => {
    if (!isNotificationSupported()) {
        return null;
    }

    if (Notification.permission !== "granted") {
        return null;
    }

    const defaultOptions: NotificationOptions = {
        body: options?.body || "",
        requireInteraction: false,
        silent: false,
    };

    try {
        const notification = new Notification(title, defaultOptions);

        notification.onclick = () => {
            window.focus();
            notification.close();
        };

        // Auto-close after 10 seconds if not interacted with
        setTimeout(() => {
            notification.close();
        }, 10000);

        return notification;
    } catch (err) {
        console.error("Failed to send notification:", err);
        return null;
    }
};

export const sendTaskOvertimeNotification = (taskName: string): Notification | null => {
    return sendNotification(`⏰ Task Overtime: ${taskName}`, {
        body: "Your task timer has exceeded the estimated time!",
        tag: "task-overtime",
    });
};

export const sendTaskCompleteNotification = (taskName: string): Notification | null => {
    return sendNotification(`✅ Task Complete: ${taskName}`, {
        body: "Great job! You finished your task!",
        tag: "task-complete",
    });
};
